import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const { settings } = useSettings();

  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("admin@travel.com");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const r = await login(email, password);

      if (r.ok) {
        toast.success("Welcome back.");

        navigate(
          location.state?.from?.pathname || "/admin",
          { replace: true }
        );
      } else {
        toast.error(r.error || "Invalid credentials");
      }
    } catch (error) {
      toast.error("Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = settings?.logo_url;

  const companyName =
    settings?.company_name || "Vizag Jagan Tours & Travels";

  const tagline =
    settings?.tagline || "Travel with Comfort, Explore with Us";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-navy-900 text-slate-100">

      {/* =========================================================
          LEFT BRANDING SECTION
      ========================================================== */}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden bg-brand-navy-950">

        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy-950 via-slate-950 to-brand-navy-900" />

        {/* Decorative glow */}
        <div className="absolute w-[550px] h-[550px] rounded-full bg-amber-400/5 blur-3xl" />

        {/* Branding */}
        <div className="relative z-10 flex flex-col items-center text-center px-12">

          {/* SITE LOGO */}
          {logoUrl && (
            <div className="w-56 h-56 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-2xl ring-1 ring-white/20">
              <img
                src={logoUrl}
                alt={companyName}
                className="w-full h-full object-contain p-3"
              />
            </div>
          )}

          {/* FALLBACK IF NO LOGO */}
          {!logoUrl && (
            <div className="w-56 h-56 rounded-full bg-white flex items-center justify-center shadow-2xl ring-1 ring-white/20">
              <div className="text-center px-6">
                <div className="font-display text-3xl text-slate-900">
                  VJ
                </div>

                <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  Tours & Travels
                </div>
              </div>
            </div>
          )}

          {/* COMPANY NAME */}
          <div className="font-display text-4xl xl:text-5xl text-white mt-10">
            {companyName}
          </div>

          {/* TAGLINE */}
          <p className="mt-4 text-slate-400 text-lg max-w-xl leading-relaxed">
            {tagline}
          </p>

          {/* GOLD DIVIDER */}
          <div className="mt-8 w-20 h-px bg-amber-400/70" />

          {/* ADMIN CONSOLE */}
          <div className="mt-8 text-xs uppercase tracking-[0.32em] text-amber-400">
            Vizag Jagan Travels
          </div>

          <div className="font-display text-4xl text-white mt-3">
            Admin Console
          </div>

          <p className="mt-4 text-slate-400 max-w-md leading-relaxed">
            Curate content, manage inquiries, and keep every detail of your
            journeys in order.
          </p>
        </div>
      </div>

      {/* =========================================================
          RIGHT LOGIN SECTION
      ========================================================== */}
      <div className="grid place-items-center p-6 sm:p-8">

        <Card className="w-full max-w-md bg-slate-900 border-slate-800 text-slate-100 shadow-2xl">

          <CardContent className="p-7 sm:p-8">

            {/* LOGIN HEADER */}
            <div className="font-display text-3xl">
              Sign in
            </div>

            <p className="text-slate-400 text-sm mt-1">
              Single admin console.
            </p>

            {/* LOGIN FORM */}
            <form
              onSubmit={submit}
              className="mt-8 space-y-5"
            >

              {/* EMAIL */}
              <div>
                <Label htmlFor="admin-email">
                  Email
                </Label>

                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="admin-email"
                  required
                />
              </div>

              {/* PASSWORD */}
              <div>
                <Label htmlFor="admin-password">
                  Password
                </Label>

                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="admin-password"
                  required
                />
              </div>

              {/* SIGN IN BUTTON */}
              <Button
                type="submit"
                className="w-full bg-amber-400 hover:bg-amber-300 text-slate-950"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading
                  ? "Signing in…"
                  : "Sign in →"}
              </Button>

              {/* DEFAULT CREDENTIALS */}
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-800">
                Default: admin@travel.com / Admin@123
              </div>

            </form>

          </CardContent>

        </Card>

      </div>

    </div>
  );
}